import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { apiService } from '../services/api';
import { Modal } from './Modal';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

interface CalendarOption {
  _id: string;
  name: string;
  isDefault?: boolean;
}

interface CalendarPickerFieldProps {
  label: string;
  value?: string;
  onChange: (calendarId: string | undefined) => void;
  /** Shown as the field's placeholder/empty state — e.g. "Inherit from business default". */
  allowClear?: boolean;
  businessId?: string;
}

/**
 * Compact calendar-picker field — same underlying calendar list and
 * default-first sort QuickReminderModal already uses (getCalendars with
 * isActive:true), but as a tap-to-open field + selection sheet suited to
 * sitting inside a settings/create form, rather than QuickReminderModal's
 * own full add-reminder flow (title/date/note). Fixes a real usability
 * bug: VisitsSetupScreen's defaultCalendarId was previously a raw text
 * input requiring the user to type/paste a calendar's ObjectId by hand.
 */
export const CalendarPickerField: React.FC<CalendarPickerFieldProps> = ({
  label,
  value,
  onChange,
  allowClear = true,
  businessId,
}) => {
  const styles = useThemedStyles(getStyles);
  const [isOpen, setIsOpen] = useState(false);
  const [calendars, setCalendars] = useState<CalendarOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || calendars.length > 0) return;
    loadCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const loadCalendars = async () => {
    setLoading(true);
    try {
      const resolvedBusinessId = businessId || apiService.getBusinessIdFromToken();
      const res = await apiService.getCalendars({ businessId: resolvedBusinessId || undefined, isActive: true });
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        setCalendars(res.data.map((c: any) => ({ _id: c._id, name: c.name, isDefault: c.isDefault })));
      }
    } catch (err) {
      console.error('[CalendarPickerField] Failed to load calendars:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedName = calendars.find((c) => c._id === value)?.name;

  return (
    <View style={styles.wrap}>
      <Text style={styles.label}>{label}</Text>
      <TouchableOpacity style={styles.field} onPress={() => setIsOpen(true)}>
        <Text style={[styles.fieldText, !selectedName && !value && styles.fieldTextPlaceholder]} numberOfLines={1}>
          {selectedName || (value ? value : allowClear ? 'Use business default' : 'Select a calendar')}
        </Text>
        <Ionicons name="chevron-down" size={16} color={colors.text.secondary as string} />
      </TouchableOpacity>

      <Modal visible={isOpen} onClose={() => setIsOpen(false)} title={label}>
        <View style={styles.sheetContent}>
          {loading ? (
            <View style={styles.loadingRow}>
              <ActivityIndicator size="small" color={colors.primary as string} />
              <Text style={styles.loadingText}>Loading calendars…</Text>
            </View>
          ) : (
            <>
              {allowClear && (
                <TouchableOpacity
                  style={[styles.option, !value && styles.optionActive]}
                  onPress={() => { onChange(undefined); setIsOpen(false); }}
                >
                  <Ionicons
                    name={!value ? 'radio-button-on' : 'radio-button-off'}
                    size={18}
                    color={!value ? (colors.primary as string) : (colors.text.secondary as string)}
                  />
                  <Text style={styles.optionText}>Use business default</Text>
                </TouchableOpacity>
              )}
              {calendars.map((c) => (
                <TouchableOpacity
                  key={c._id}
                  style={[styles.option, value === c._id && styles.optionActive]}
                  onPress={() => { onChange(c._id); setIsOpen(false); }}
                >
                  <Ionicons
                    name={value === c._id ? 'radio-button-on' : 'radio-button-off'}
                    size={18}
                    color={value === c._id ? (colors.primary as string) : (colors.text.secondary as string)}
                  />
                  <Text style={styles.optionText}>{c.name}</Text>
                  {c.isDefault && <Text style={styles.defaultBadge}>Default</Text>}
                </TouchableOpacity>
              ))}
              {calendars.length === 0 && (
                <Text style={styles.emptyText}>No calendars found — create one first.</Text>
              )}
            </>
          )}
        </View>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrap: { marginBottom: spacing.md },
  label: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.semibold, color: colors.text.secondary as string, marginBottom: spacing.xs },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface as string,
  },
  fieldText: { fontSize: typography.fontSize.base, color: colors.text.primary as string, flex: 1 },
  fieldTextPlaceholder: { color: colors.text.secondary as string },
  sheetContent: { padding: spacing.md, gap: spacing.xs },
  loadingRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md },
  loadingText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border as string,
    backgroundColor: colors.background.secondary as string,
  },
  optionActive: { borderColor: colors.primary as string, backgroundColor: withAlpha(colors.primary, 0.06) },
  optionText: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary as string },
  defaultBadge: { fontSize: typography.fontSize.xs, color: colors.primary as string, fontFamily: typography.fontFamily.semibold },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string, padding: spacing.md },
});
